import { useCallback, useEffect, useState } from 'react';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { useRealtimeTable } from './useRealtimeTable';

// CMS Audio-Liste — HLS-Encoding-Status als Tabellenspalte (statt separatem Panel).
//
// Der HLS-Status lebt NICHT auf den Audio-Content-Rows aus /api/audio, sondern in der
// separaten `audio_tracks`-Tabelle (RLS: staff-only SELECT, wp5-realtime-publication.sql).
// Diese Hook abonniert `audio_tracks` live (Realtime-Logik aus dem ehemaligen
// HlsEncodingStatus-Panel adaptiert) und liefert eine
// Map, gekeyed nach `title` — dem einzigen gemeinsamen Feld zwischen audio_tracks und der
// Content-Liste (audio_tracks trägt nur id/title/hlsStatus, kein Content-Fremdschlüssel).
// title-Kollisionen: der neueste Track (nach updatedAt) gewinnt.
//
// Die HLS-Pipeline (Bunny-Callback → Edge-Function) schreibt hlsStatus erst ab WP6; bis dahin
// ist die Tabelle in dev leer und die Spalte zeigt „–".

/* ────────────────────────────────────────────────────────────────────────────────────────────
 * Prisma-gebundene Werteliste (Schnitt D/E aus docs/plans/plan-vertragstreue-2026-08.md)
 *
 * Diese Liste steht NICHT frei. `src/lib/__tests__/hlsStatusVertrag.test.ts` liest
 * `energetic-eternity-backend/prisma/schema.prisma` und vergleicht sie Glied für Glied gegen
 * `enum HlsStatus`. Wer hier etwas ergänzt, streicht oder umbenennt, ohne dass Prisma es hergibt,
 * bekommt einen roten Lauf — und umgekehrt.
 *
 * Warum `as const`-Array statt einer bloßen Union: eine Union existiert zur Laufzeit nicht, ein
 * Test kann sie also nicht lesen. Der Vertrag braucht auf BEIDEN Seiten eine Liste; die Union wird
 * aus der Liste abgeleitet und kann deshalb nicht von ihr abweichen. Dieselbe Form steht im Web
 * (`web/apps/shared/types/player.ts`, `HLS_STATUS_WERTE`).
 *
 * ⚠️ EHRLICHE BEGRÜNDUNG FÜR `NONE`, und sie unterscheidet sich von der im Web: dort trägt
 * `tracks.hlsStatus` `@default(NONE)` (schema.prisma:845) und `TrackService.toDto` reicht den Wert
 * ungefiltert durch — `NONE` kommt dort nachweislich an. Dieser Hook liest etwas anderes, nämlich
 * die Tabelle `audio_tracks` direkt über Supabase, und deren Default ist `PENDING`
 * (schema.prisma:790). `NONE` ist hier also nur TYP-möglich (die Spalte trägt den Typ `HlsStatus`),
 * nicht gemessen. Aufgenommen wird er trotzdem — aus Vollständigkeit gegenüber dem Prisma-Enum,
 * nicht wegen gemessener Daten. Der Unterschied steht hier, damit ihn niemand als Messung liest.
 *
 * Verhalten unverändert: `AudioManagement.getHlsBadge` ist ein `switch` mit `default`-Zweig —
 * `NONE` fällt dort auf „–", genau wie ein fehlender Eintrag. Kein `Record<HlsStatus, …>` im Baum,
 * das erschöpfend brechen könnte.
 * ──────────────────────────────────────────────────────────────────────────────────────────── */
export const HLS_STATUS_WERTE = ['NONE', 'PENDING', 'READY', 'FAILED'] as const;
export type HlsStatus = (typeof HLS_STATUS_WERTE)[number];

interface AudioTrackRow extends Record<string, unknown> {
  id: string;
  title: string;
  hlsStatus: HlsStatus;
  hlsError: string | null;
  hlsUpdatedAt: string | null;
  updatedAt: string;
}

export interface HlsStatusEntry {
  status: HlsStatus;
  error: string | null;
  updatedAt: string;
}

const SELECT_COLUMNS = 'id, title, hlsStatus, hlsError, hlsUpdatedAt, updatedAt';
const MAX_ROWS = 200;

// Reduziert eine Track-Liste auf eine title→Entry-Map (neuester Track pro Titel gewinnt).
const buildMap = (rows: AudioTrackRow[]): Map<string, HlsStatusEntry> => {
  const byTitle = new Map<string, HlsStatusEntry>();
  for (const row of rows) {
    if (!row?.title) continue;
    const existing = byTitle.get(row.title);
    if (!existing || row.updatedAt >= existing.updatedAt) {
      byTitle.set(row.title, {
        status: row.hlsStatus,
        error: row.hlsError ?? null,
        updatedAt: row.updatedAt,
      });
    }
  }
  return byTitle;
};

/**
 * Live-Map des HLS-Encoding-Status, gekeyed nach Audio-Titel.
 * Verwendung: `const hlsMap = useHlsStatusMap(); const entry = hlsMap.get(audio.title);`
 */
export function useHlsStatusMap(): Map<string, HlsStatusEntry> {
  const [tracks, setTracks] = useState<AudioTrackRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('audio_tracks')
        .select(SELECT_COLUMNS)
        .order('updatedAt', { ascending: false })
        .limit(MAX_ROWS);
      if (cancelled || error) return;
      const snapshot = (data as AudioTrackRow[]) ?? [];
      // Snapshot UNTER bereits live-gepatchte Rows mergen (live gewinnt bei jüngerem updatedAt),
      // damit ein langsamer Initial-Load kein neueres Live-Update überschreibt.
      setTracks((prev) => {
        const byId = new Map<string, AudioTrackRow>(snapshot.map((r) => [r.id, r]));
        for (const r of prev) {
          const existing = byId.get(r.id);
          if (!existing || r.updatedAt >= existing.updatedAt) byId.set(r.id, r);
        }
        return Array.from(byId.values());
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = useCallback((payload: RealtimePostgresChangesPayload<AudioTrackRow>) => {
    setTracks((prev) => {
      if (payload.eventType === 'DELETE') {
        const deletedId = (payload.old as Partial<AudioTrackRow>).id;
        return deletedId ? prev.filter((t) => t.id !== deletedId) : prev;
      }
      const row = payload.new as AudioTrackRow;
      if (!row?.id) return prev;
      return [...prev.filter((t) => t.id !== row.id), row];
    });
  }, []);

  useRealtimeTable<AudioTrackRow>('audio_tracks', handleChange);

  return buildMap(tracks);
}
